export const LIVE_CANONICAL_ROOT_URL = 'https://sweetlisteners.com';

export const cleanRootUrl = value =>
  typeof value === 'string' ? value.replace(/\/$/, '') : '';

const isLiveCanonicalHost = value => {
  const cleaned = cleanRootUrl(value);
  if (!cleaned) {
    return false;
  }
  try {
    const host = new URL(cleaned).hostname.toLowerCase();
    return host === 'sweetlisteners.com' || host === 'www.sweetlisteners.com';
  } catch (e) {
    return false;
  }
};

/**
 * Canonical origin for <link rel="canonical">, og:url, and JSON-LD.
 * Always the Live host. Never fly.dev, mysharetribe, or localhost.
 */
export const resolveCanonicalRootURL = ({ canonicalRootURL } = {}) => {
  if (isLiveCanonicalHost(canonicalRootURL)) {
    return LIVE_CANONICAL_ROOT_URL;
  }
  return LIVE_CANONICAL_ROOT_URL;
};

const SIGNED_IN_NOINDEX_PREFIXES = [
  '/dashboard',
  '/availability',
  '/favorites',
  '/past-sessions',
  '/notifications',
  '/calendar',
  '/earnings',
  '/inbox',
  '/bookings',
  '/order',
  '/sale',
  '/listings',
  '/account',
  '/profile',
  '/profile-settings',
  '/reset-password',
  '/verify-email',
  '/confirm',
  '/choose-role',
  '/recover-password',
  '/preview',
  '/styleguide',
  '/ops',
];

const SIGNED_IN_LISTING_SUFFIXES = [
  'checkout',
  'book',
  'draft',
  'pending-approval',
  'edit',
  'new',
  'make-offer',
  'request-quote',
];

/**
 * Dashboard, inbox, checkout, account, and session pages must not be indexed
 * even on production.
 */
export const isSignedInNoIndexPath = pathname => {
  const path = typeof pathname === 'string' ? pathname.split('?')[0] : '';
  if (!path) {
    return false;
  }
  if (path === '/l/new' || path.startsWith('/l/new/')) {
    return true;
  }
  if (SIGNED_IN_NOINDEX_PREFIXES.some(prefix => path === prefix || path.startsWith(`${prefix}/`))) {
    return true;
  }
  const listingParts = path.split('/').filter(Boolean);
  if (listingParts[0] === 'l' && listingParts.length >= 3) {
    return listingParts.some(part => SIGNED_IN_LISTING_SUFFIXES.includes(part));
  }
  return false;
};

export const absoluteAssetUrl = (url, root) => {
  if (!url || typeof url !== 'string') {
    return url;
  }
  if (/^https?:\/\//i.test(url) || url.startsWith('//')) {
    return url;
  }
  const base = (root || '').replace(/\/$/, '');
  if (!base) {
    return url;
  }
  return `${base}${url.startsWith('/') ? url : `/${url}`}`;
};

const ensureOpenGraphLocale = locale => {
  switch (locale) {
    case 'en':
      return 'en_US';
    default:
      return locale;
  }
};

/**
 * These will be used with Helmet <meta {...openGraphMetaProps} />
 */
export const openGraphMetaProps = data => {
  const {
    openGraphType,
    socialSharingTitle,
    socialSharingDescription,
    published,
    updated,
    url,
    locale,
    facebookImages,
    facebookAppId,
    marketplaceName,
    marketplaceRootURL,
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && openGraphType && url && facebookImages)) {
    if (console && console.warn) {
      console.warn(
        `Can't create Open Graph meta tags:
        socialSharingTitle, socialSharingDescription, openGraphType, url, and facebookImages are needed.`
      );
    }
    return [];
  }

  const openGraphMeta = [
    { property: 'og:description', content: socialSharingDescription },
    { property: 'og:title', content: socialSharingTitle },
    { property: 'og:type', content: openGraphType },
    { property: 'og:url', content: url },
    { property: 'og:locale', content: ensureOpenGraphLocale(locale) },
  ];

  if (facebookImages && facebookImages.length > 0) {
    facebookImages.forEach(i => {
      openGraphMeta.push({
        property: 'og:image',
        content: absoluteAssetUrl(i.url, marketplaceRootURL),
      });

      if (i.width && i.height) {
        openGraphMeta.push({ property: 'og:image:width', content: i.width });
        openGraphMeta.push({ property: 'og:image:height', content: i.height });
      }
    });
  }

  if (marketplaceName) {
    openGraphMeta.push({ property: 'og:site_name', content: marketplaceName });
  }

  if (facebookAppId) {
    openGraphMeta.push({ property: 'fb:app_id', content: facebookAppId });
  }

  if (published) {
    openGraphMeta.push({ property: 'article:published_time', content: published });
  }

  if (updated) {
    openGraphMeta.push({ property: 'article:modified_time', content: updated });
  }

  return openGraphMeta;
};

/**
 * These will be used with Helmet <meta {...twitterMetaProps} />
 */
export const twitterMetaProps = data => {
  const {
    socialSharingTitle,
    socialSharingDescription,
    twitterHandle,
    twitterImages,
    url,
    marketplaceRootURL,
    siteTwitterHandle,
  } = data;

  if (!(socialSharingTitle && socialSharingDescription && url)) {
    if (console && console.warn) {
      console.warn(
        `Can't create twitter card meta tags:
        socialSharingTitle, socialSharingDescription, and url are needed.`
      );
    }
    return [];
  }

  const twitterMeta = [
    { name: 'twitter:card', content: 'summary_large_image' },
    { name: 'twitter:title', content: socialSharingTitle },
    { name: 'twitter:description', content: socialSharingDescription },
    { name: 'twitter:url', content: url },
  ];

  if (siteTwitterHandle) {
    twitterMeta.push({ name: 'twitter:site', content: siteTwitterHandle });
  }

  if (twitterImages && twitterImages.length > 0) {
    twitterImages.forEach(i => {
      twitterMeta.push({
        name: 'twitter:image',
        content: absoluteAssetUrl(i.url, marketplaceRootURL),
      });
    });
  }

  if (twitterHandle) {
    // TODO: If we want to connect providers twitter account on ListingPage
    // we needs to get this info among listing data (API support needed)
    twitterMeta.push({ name: 'twitter:creator', content: twitterHandle });
  }

  if (marketplaceRootURL) {
    twitterMeta.push({
      name: 'twitter:domain',
      content: marketplaceRootURL.replace(/^https?:\/\//i, '').replace(/\/$/, ''),
    });
  }

  return twitterMeta;
};

/**
 * These will be used with Helmet <meta {...metaTagProps} />
 * Creates data for Open Graph and Twitter meta tags.
 */
export const metaTagProps = (tagData, config) => {
  const { facebookAppId, marketplaceName, siteTwitterHandle, googleSearchConsole } = config;
  const canonicalRoot = resolveCanonicalRootURL(config);

  const author = tagData.author || marketplaceName;
  const googleSiteVerification = googleSearchConsole?.googleSiteVerification;
  const googleSiteVerificationMaybe = googleSiteVerification
    ? [{ name: 'google-site-verification', content: googleSiteVerification }]
    : [];
  const defaultMeta = [
    { name: 'description', content: tagData.description },
    { name: 'author', content: author },
    ...googleSiteVerificationMaybe,
  ];

  if (tagData.noIndex) {
    defaultMeta.push({ name: 'robots', content: 'noindex' });
  }

  const openGraphMeta = openGraphMetaProps({
    ...tagData,
    facebookAppId,
    marketplaceName,
    marketplaceRootURL: canonicalRoot,
  });

  const twitterMeta = twitterMetaProps({
    ...tagData,
    marketplaceRootURL: canonicalRoot,
    siteTwitterHandle,
  });

  return [...defaultMeta, ...openGraphMeta, ...twitterMeta];
};
